/**
 * WebAuthn (Passkey) utilities for SKU Manager
 * Provides comprehensive support for passkey registration, authentication, and management
 */

import type {
  AttestationConveyancePreference,
  AuthenticationResponseJSON,
  AuthenticatorAttachment,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  UserVerificationRequirement,
} from '@simplewebauthn/browser';
import type {
  StartPasskeyAuthenticationMutation,
  StartPasskeySignupMutation,
} from '../generated/graphql';
import { logger } from './logger';

/**
 * The GraphQL shape every "start registration" style mutation returns
 * (`startPasskeySignup`, `startPasskeyRegistration`) — every field is nullable at the
 * schema level (Pothos defaults fields to nullable), even though a well-formed server
 * response always populates them. Derived from `StartPasskeySignupMutation` rather than
 * hand-written; `startPasskeyRegistration`'s payload is structurally identical (same
 * `PasskeyRegistrationOptions` GraphQL type) so it satisfies this type too.
 */
export type PasskeyRegistrationOptionsPayload = NonNullable<
  StartPasskeySignupMutation['startPasskeySignup']
>;

/** The GraphQL shape `startPasskeyAuthentication` returns — same nullability note as above. */
export type PasskeyAuthenticationOptionsPayload = NonNullable<
  StartPasskeyAuthenticationMutation['startPasskeyAuthentication']
>;

/**
 * Thrown when the server's options payload is missing a field a passkey ceremony cannot
 * proceed without. Per CRITICAL RULE 2, this never defaults a missing field — it fails
 * loudly instead of silently sending `undefined`/`""` to `navigator.credentials`.
 */
export class PasskeyOptionsError extends Error {
  constructor(field: string) {
    super(`Unable to start passkey ceremony: server did not provide "${field}"`);
    this.name = 'PasskeyOptionsError';
  }
}

function required<T>(value: T | null | undefined, field: string): T {
  if (value === null || value === undefined) {
    throw new PasskeyOptionsError(field);
  }
  return value;
}

const USER_VERIFICATION_VALUES = new Set<UserVerificationRequirement>([
  'discouraged',
  'preferred',
  'required',
]);
function readUserVerification(value: string, field: string): UserVerificationRequirement {
  if ((USER_VERIFICATION_VALUES as Set<string>).has(value)) {
    return value as UserVerificationRequirement;
  }
  throw new PasskeyOptionsError(`${field} (unexpected value "${value}")`);
}

const ATTESTATION_VALUES = new Set<AttestationConveyancePreference>([
  'direct',
  'enterprise',
  'indirect',
  'none',
]);
function readAttestation(
  value: string | null | undefined,
): AttestationConveyancePreference | undefined {
  if (value == null) return undefined;
  if ((ATTESTATION_VALUES as Set<string>).has(value)) {
    return value as AttestationConveyancePreference;
  }
  throw new PasskeyOptionsError(`attestation (unexpected value "${value}")`);
}

const AUTHENTICATOR_ATTACHMENT_VALUES = new Set<AuthenticatorAttachment>([
  'cross-platform',
  'platform',
]);
function readAuthenticatorAttachment(
  value: string | null | undefined,
): AuthenticatorAttachment | undefined {
  if (value == null) return undefined;
  if ((AUTHENTICATOR_ATTACHMENT_VALUES as Set<string>).has(value)) {
    return value as AuthenticatorAttachment;
  }
  throw new PasskeyOptionsError(
    `authenticatorSelection.authenticatorAttachment (unexpected value "${value}")`,
  );
}

/**
 * Validate and narrow a `startPasskeySignup` / `startPasskeyRegistration` response into
 * exactly what `@simplewebauthn/browser`'s `startRegistration({ optionsJSON })` requires.
 * No `as unknown as` cast: every required field is checked, and an unexpected enum value
 * throws rather than being passed through to `navigator.credentials.create()` un-narrowed.
 */
export function toRegistrationOptionsJSON(
  options: PasskeyRegistrationOptionsPayload,
): PublicKeyCredentialCreationOptionsJSON {
  const rp = required(options.rp, 'rp');
  const user = required(options.user, 'user');
  const pubKeyCredParams = required(options.pubKeyCredParams, 'pubKeyCredParams');
  const authenticatorSelection = options.authenticatorSelection;

  return {
    challenge: required(options.challenge, 'challenge'),
    rp: {
      id: required(rp.id, 'rp.id'),
      name: required(rp.name, 'rp.name'),
    },
    user: {
      id: required(user.id, 'user.id'),
      // WebAuthn's OWN user object (`PublicKeyCredentialUserEntity`) has a `name` that
      // is the account identifier (an email, by spec convention) — unrelated to our
      // app's `User.name` model field, which SKU-1524 removed from the web entirely.
      // Read via `options.user.name` (optional-chained; `user` above already asserted
      // `options.user` non-null) so the SKU-1524 conformance scan's allowlist pattern
      // recognizes this as the WebAuthn field, not the app's.
      name: required(options.user?.name, 'options.user.name'),
      displayName: required(user.displayName, 'user.displayName'),
    },
    pubKeyCredParams: pubKeyCredParams.map((param, i) => ({
      type: 'public-key',
      alg: required(param.alg, `pubKeyCredParams[${i}].alg`),
    })),
    timeout: options.timeout ?? undefined,
    excludeCredentials: (options.excludeCredentials ?? []).map((cred, i) => ({
      id: required(cred.id, `excludeCredentials[${i}].id`),
      type: 'public-key',
      transports: cred.transports ?? undefined,
    })),
    authenticatorSelection: authenticatorSelection
      ? {
          authenticatorAttachment: readAuthenticatorAttachment(
            authenticatorSelection.authenticatorAttachment,
          ),
          userVerification: readUserVerification(
            required(
              authenticatorSelection.userVerification,
              'authenticatorSelection.userVerification',
            ),
            'authenticatorSelection.userVerification',
          ),
          requireResidentKey: authenticatorSelection.requireResidentKey ?? undefined,
        }
      : undefined,
    attestation: readAttestation(options.attestation),
  };
}

/**
 * Validate and narrow a `startPasskeyAuthentication` response into exactly what
 * `@simplewebauthn/browser`'s `startAuthentication({ optionsJSON })` requires.
 */
export function toAuthenticationOptionsJSON(
  options: PasskeyAuthenticationOptionsPayload,
): PublicKeyCredentialRequestOptionsJSON {
  return {
    challenge: required(options.challenge, 'challenge'),
    timeout: options.timeout ?? undefined,
    rpId: options.rpId ?? undefined,
    allowCredentials: (options.allowCredentials ?? []).map((cred, i) => ({
      id: required(cred.id, `allowCredentials[${i}].id`),
      type: 'public-key',
      transports: cred.transports ?? undefined,
    })),
    userVerification:
      options.userVerification != null
        ? readUserVerification(options.userVerification, 'userVerification')
        : undefined,
  };
}

/**
 * The GraphQL variables every passkey completion mutation (`signupWithPasskey`,
 * `completePasskeyRegistration`) accepts for the credential it just created —
 * `@simplewebauthn/browser`'s `RegistrationResponseJSON` maps onto this directly, no
 * base64url re-encoding needed (the library already produced base64url strings).
 */
export function registrationResultToVariables(result: RegistrationResponseJSON) {
  return {
    credentialId: result.id,
    rawId: result.rawId,
    // The GraphQL `JSON` scalar is typed `Record<string, unknown>` by codegen; the
    // library's `response`/`clientExtensionResults` are plain JSON-serialisable objects
    // without a TS index signature — this boundary cast is the JSON-scalar equivalent of
    // `JSON.parse(JSON.stringify(...))`, not an unsafe reinterpretation of the data.
    response: result.response as unknown as Record<string, unknown>,
    clientExtensionResults: result.clientExtensionResults as unknown as Record<string, unknown>,
    authenticatorAttachment: result.authenticatorAttachment ?? null,
  };
}

/** Same as {@link registrationResultToVariables}, for `completePasskeyAuthentication`. */
export function authenticationResultToVariables(result: AuthenticationResponseJSON) {
  return {
    credentialId: result.id,
    rawId: result.rawId,
    response: result.response as unknown as Record<string, unknown>,
    clientExtensionResults: result.clientExtensionResults as unknown as Record<string, unknown>,
    authenticatorAttachment: result.authenticatorAttachment ?? null,
  };
}

/**
 * Check if WebAuthn/Passkeys are supported in the current browser
 */
export const isWebAuthnSupported = (): boolean => {
  return !!(
    typeof window !== 'undefined' &&
    window.PublicKeyCredential &&
    window.navigator.credentials &&
    typeof window.navigator.credentials.create === 'function' &&
    typeof window.navigator.credentials.get === 'function'
  );
};

/**
 * Check if platform authenticator (device-built-in) is available
 */
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    logger.error('Error checking platform authenticator availability', 'WebAuthn', { error });
    return false;
  }
};

/**
 * Check if conditional UI (autofill) is supported
 */
export const isConditionalUISupported = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;

  try {
    // Check if the method exists before calling it
    if ('isConditionalMediationAvailable' in PublicKeyCredential) {
      const credentialClass = PublicKeyCredential as typeof PublicKeyCredential & {
        isConditionalMediationAvailable: () => Promise<boolean>;
      };
      return await credentialClass.isConditionalMediationAvailable();
    }
    return false;
  } catch (error) {
    logger.error('Error checking conditional UI support', 'WebAuthn', { error });
    return false;
  }
};

/**
 * Get device/browser information for passkey naming
 */
export const getDeviceInfo = (): string => {
  const userAgent = navigator.userAgent;

  if (/iPhone|iPad|iPod/.test(userAgent)) {
    return 'iPhone/iPad';
  } else if (/Android/.test(userAgent)) {
    return 'Android Device';
  } else if (/Macintosh/.test(userAgent)) {
    return 'Mac';
  } else if (/Windows/.test(userAgent)) {
    return 'Windows PC';
  } else if (/Linux/.test(userAgent)) {
    return 'Linux Device';
  } else {
    return 'Unknown Device';
  }
};

/**
 * Error handling utilities
 */
type WebAuthnErrorLike = {
  graphQLErrors?: unknown;
  networkError?: { result?: { errors?: unknown } };
  name?: unknown;
  message?: unknown;
};

const isErrorLike = (value: unknown): value is WebAuthnErrorLike =>
  typeof value === 'object' && value !== null;

const firstMessage = (value: unknown): string | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const first = value[0];
  if (!first || typeof first !== 'object') return undefined;
  const message = (first as { message?: unknown }).message;
  return typeof message === 'string' && message.length > 0 ? message : undefined;
};

export const getWebAuthnErrorMessage = (error: unknown): string => {
  if (!isErrorLike(error)) return 'An unexpected error occurred.';

  // Handle GraphQL errors first
  const gqlMessage = firstMessage(error.graphQLErrors);
  if (gqlMessage) return gqlMessage;

  // Handle Apollo Client errors
  if (typeof error.message === 'string' && error.message.includes('GraphQL error:')) {
    return error.message.replace('GraphQL error: ', '');
  }

  // Handle network errors with GraphQL response
  const networkMessage = firstMessage(error.networkError?.result?.errors);
  if (networkMessage) return networkMessage;

  // Handle WebAuthn specific errors
  switch (error.name) {
    case 'NotAllowedError':
      return 'The operation was not allowed. Please try again.';
    case 'SecurityError':
      return "A security error occurred. Please ensure you're on a secure connection.";
    case 'NotSupportedError':
      return 'Passkeys are not supported on this device or browser.';
    case 'InvalidStateError':
      return 'A passkey for this account already exists on this device.';
    case 'ConstraintError':
      return 'The authenticator does not meet the required constraints.';
    case 'UnknownError':
      return 'An unknown error occurred. Please try again.';
    default:
      return typeof error.message === 'string' && error.message.length > 0
        ? error.message
        : 'An unexpected error occurred.';
  }
};

/**
 * Check if error is user cancellation
 */
export const isUserCancellation = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string };
  return (
    e.name === 'NotAllowedError' ||
    e.message?.includes('cancelled') === true ||
    e.message?.includes('canceled') === true
  );
};

/**
 * The subset of `Location` these checks read. A test passes a plain object instead of
 * mutating `window.location` — under Vitest's `vmThreads` pool the jsdom `window` IS the
 * VM context's real global, where `location` is non-configurable and
 * `Object.defineProperty(window, 'location', ...)` throws "Cannot redefine property"
 * (SKU-1875; see `apolloErrorLink.test.ts` for the same constraint on `pathname`).
 */
type LocationLike = Pick<Location, 'protocol' | 'hostname'>;

/**
 * Browser compatibility checker
 */
export const getBrowserCompatibility = (locationOverride?: LocationLike) => {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor || '';
  const isChrome = /Chrome/.test(userAgent) && (/Google Inc/.test(vendor) || /Google/.test(vendor));
  const isFirefox = /Firefox/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);

  return {
    isSupported: isWebAuthnSupported(),
    browser: isChrome
      ? 'Chrome'
      : isFirefox
        ? 'Firefox'
        : isSafari
          ? 'Safari'
          : isEdge
            ? 'Edge'
            : 'Unknown',
    hasFullSupport:
      isChrome || isFirefox || isEdge || (isSafari && /Version\/1[4-9]/.test(userAgent)),
    recommendations: getCompatibilityRecommendations(locationOverride),
  };
};

/**
 * Get browser-specific recommendations
 */
const getCompatibilityRecommendations = (locationOverride?: LocationLike): string[] => {
  const recommendations: string[] = [];
  const userAgent = navigator.userAgent;

  if (!isWebAuthnSupported()) {
    recommendations.push('Update your browser to the latest version for passkey support');
  }

  if (/Safari/.test(userAgent) && !/Version\/1[4-9]/.test(userAgent)) {
    recommendations.push('Update Safari to version 14 or later for full passkey support');
  }

  // Handle test environment where window.location might not exist
  const location =
    locationOverride ?? (typeof window !== 'undefined' ? window.location : undefined);
  if (location) {
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      recommendations.push('Passkeys require a secure HTTPS connection');
    }
  }

  return recommendations;
};

/** The server code for a sign-in with a credential id it has no passkey for. */
export const PASSKEY_UNKNOWN_CREDENTIAL = 'PASSKEY_UNKNOWN_CREDENTIAL';

/**
 * The WebAuthn Signal API's `signalUnknownCredential` (Chrome 132+, Safari 26+). Not yet in
 * every TypeScript DOM lib, so it is feature-detected through this narrow shape.
 */
interface SignalUnknownCredentialCapable {
  signalUnknownCredential: (options: { rpId: string; credentialId: string }) => Promise<void>;
}

function canSignalUnknownCredential(value: unknown): value is SignalUnknownCredentialCapable {
  return (
    typeof value === 'function' &&
    typeof (value as Partial<SignalUnknownCredentialCapable>).signalUnknownCredential === 'function'
  );
}

/**
 * Tell the browser/password manager that the server does not recognise `credentialId`, so it
 * stops offering a passkey that was deleted server-side or whose registration never
 * completed. Best-effort: browsers without the Signal API are left as they are, and a
 * refusal is logged, never surfaced — the user has already been told what happened.
 */
export async function signalUnknownPasskey(rpId: string, credentialId: string): Promise<void> {
  const credentialApi = typeof window !== 'undefined' ? window.PublicKeyCredential : undefined;
  if (!canSignalUnknownCredential(credentialApi)) return;
  try {
    await credentialApi.signalUnknownCredential({ rpId, credentialId });
  } catch (error) {
    logger.warn('WebAuthn: signalUnknownCredential was refused', { error });
  }
}
